import { Listing, ListingSource, SearchSummary } from './types';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

const PROVIDER_NAMES: Record<string, string> = {
  RightMove: 'Rightmove',
  Zoopla: 'Zoopla',
  OpenRent: 'OpenRent',
};

const FURNISH_NAMES: Record<string, string> = {
  FURNISHED: 'Furnished',
  PART_FURNISHED: 'Part-furnished',
  UNFURNISHED: 'Unfurnished',
};

export function money(amount: number): string {
  return '£' + amount.toLocaleString('en-GB');
}

export function ago(publishedAt: string, now: Date = new Date()): string {
  const elapsed = now.getTime() - new Date(publishedAt).getTime();
  if (elapsed < MINUTE) return 'Just now';
  if (elapsed < HOUR) return `${Math.floor(elapsed / MINUTE)} min ago`;
  if (elapsed < DAY) return `${Math.floor(elapsed / HOUR)} h ago`;
  if (elapsed < 2 * DAY) return 'Yesterday';
  return `${Math.floor(elapsed / DAY)} days ago`;
}

export function isNew(publishedAt: string, now: Date = new Date()): boolean {
  return now.getTime() - new Date(publishedAt).getTime() < DAY;
}

export function bedroomsLabel(bedrooms: number): string {
  return bedrooms === 0 ? 'Studio' : `${bedrooms} bed`;
}

export function facts(listing: Listing): string {
  const parts = [bedroomsLabel(listing.bedrooms)];
  if (listing.bathrooms !== null) parts.push(`${listing.bathrooms} bath`);
  if (listing.sizeSqm !== null) parts.push(`${listing.sizeSqm} m²`);
  return parts.join(' · ');
}

export function providerName(provider: string): string {
  return PROVIDER_NAMES[provider] ?? provider;
}

export function sourcesLabel(sources: ListingSource[]): string {
  return sources.map(source => providerName(source.provider)).join(' + ');
}

export function furnishingLabel(furnishTypes: string[]): string {
  if (!furnishTypes.length) return 'Not stated';
  return furnishTypes.map(type => FURNISH_NAMES[type] ?? type).join(' or ');
}

export function availabilityLabel(availability: Listing['availability']): string {
  if (!availability) return 'Not stated';
  if (availability.type === 'IMMEDIATELY' || !availability.availableFrom) return 'Now';
  return new Date(availability.availableFrom).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export function sqft(sqm: number): number {
  return Math.round(sqm * 10.764);
}

export function searchLabel(search: SearchSummary): string {
  const names = search.locations.length ? search.locations : search.postcodes;
  const shown = names.slice(0, 2).join(', ');
  const rest = names.length - 2;
  return rest > 0 ? `${shown} +${rest}` : shown;
}

export function searchDetail(search: SearchSummary): string {
  return [bedroomsRangeLabel(search), budgetLabel(search)].join(' · ');
}

function bedroomsRangeLabel({ minBedrooms, maxBedrooms }: SearchSummary): string {
  if (minBedrooms === null && maxBedrooms === null) return 'Any size';
  if (minBedrooms === maxBedrooms) return bedroomsLabel(minBedrooms as number);
  const min = minBedrooms ?? 0;
  if (maxBedrooms === null) return `${min}+ bed`;
  return `${min === 0 ? 'Studio' : min}–${maxBedrooms} bed`;
}

function budgetLabel({ minPrice, maxPrice }: SearchSummary): string {
  if (minPrice === null && maxPrice === null) return 'any budget';
  if (minPrice === null || minPrice === 0) return `up to ${money(maxPrice as number)}`;
  if (maxPrice === null) return `from ${money(minPrice)}`;
  return `${money(minPrice)}–${money(maxPrice)}`;
}
